/**
 * NeedsActionCard — primary content card on the dashboard.
 *
 * Renders the list of filings where ownership == viewer's role. If the list
 * is empty we render a subtle "caught up" success state rather than hide the
 * card — consistent with the HeroStrip "Awaiting you" copy.
 */

import React from "react";
import { CheckCircle2 } from "lucide-react";

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/shared/ui/card";
import type { Filing } from "@/api/user";
import type { Jurisdiction } from "@/components/common/statusTaxonomy";

import { FilingActionRow } from "./FilingActionRow";

interface NeedsActionCardProps {
  items: Array<{ filing: Filing; jurisdiction: Jurisdiction }>;
  viewerRoleLabel: string;
}

export const NeedsActionCard: React.FC<NeedsActionCardProps> = ({
  items,
  viewerRoleLabel,
}) => (
  <Card density="default" className="gap-5">
    <CardHeader>
      <div className="flex items-baseline justify-between gap-4">
        <CardTitle className="text-base">Filings needing your action</CardTitle>
        <span
          className="text-xs text-muted-foreground tabular-nums"
          aria-live="polite"
        >
          {items.length} open · {viewerRoleLabel}
        </span>
      </div>
      <CardDescription>
        Buildings waiting on you, sorted by deadline.
      </CardDescription>
    </CardHeader>

    {items.length === 0 ? (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        <CheckCircle2
          className="h-5 w-5 [color:hsl(var(--success))]"
          aria-hidden="true"
        />
        <span>
          You&rsquo;re all caught up. Nothing is blocked on you right now.
        </span>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-3">
        {items.map(({ filing, jurisdiction }) => (
          <FilingActionRow
            key={`${jurisdiction}-${filing.id}`}
            filing={filing}
            jurisdiction={jurisdiction}
          />
        ))}
      </div>
    )}
  </Card>
);

export default NeedsActionCard;
